import { CloudArrowDownIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { useRef, useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { InfoTip } from '@/components/info-tip';
import { Button } from '@/components/ui/button';
import { tracks as trackRepo } from '@/lib/db';
import { formatLocalDate, formatLocalTime } from '@/lib/format';
import type { CostEstimate, SourceError, TrackCandidate, TrackSource } from '@/lib/sources';

import { alreadyHeld, fetchAndImport, type FetchImportResult } from './fetch-import';

/**
 * What a source holds, and one button per row to go and get it.
 *
 * This is the half of the fetch screen that is not the search form, and it is
 * its own component because two places now arrive at a list of candidates by
 * different routes: `SourceSearchPanel`, where the pilot typed a registration
 * and a date range, and the startup check, which searched on its own. Both owe
 * the pilot the same thing — what was found, what it costs, which ones are
 * already held, and *nothing downloaded until they say so* — and the second one
 * exists precisely so that the check no longer downloads anything itself.
 *
 * Row state lives here rather than in either caller: "fetching", "imported",
 * "already had it" and "that failed" are facts about this list on screen, not
 * about the search or the check that produced it.
 *
 * "Fetch all" walks the same per-row path one candidate at a time. Sequential
 * because the alternative spends a whole search's worth of credits in one
 * burst at a source that rate-limits, and because a pilot who has seen enough
 * can stop it between flights. It skips what is already held — the rows that
 * say so keep their individual "Fetch anyway" — and it stops on its own when a
 * failure would repeat for every flight behind it.
 */

export function CostLine({ estimate }: { estimate: CostEstimate | null | undefined }) {
  if (estimate === null || estimate === undefined) {
    return null;
  }
  /*
   * The first clause on the line, the whole explanation behind (i). A
   * source's description runs to three sentences — request count, how it
   * charges, its licence — and printed in full it was the longest thing on
   * the fetch form. The count is what decides whether to press Search.
   */
  const head = estimate.description.split(/ — |\. /)[0] ?? estimate.description;
  const more = head !== estimate.description;
  return (
    <p className="text-muted-foreground flex flex-wrap items-center gap-1 text-xs">
      <span>
        {estimate.credits > 0 ? (
          <span className="text-foreground">
            About {estimate.credits} {estimate.credits === 1 ? 'credit' : 'credits'}.{' '}
          </span>
        ) : null}
        {more ? `${head.replace(/\.$/, '')}.` : head}
      </span>
      {more ? (
        <InfoTip label="About this request">
          <p>{estimate.description}</p>
        </InfoTip>
      ) : null}
    </p>
  );
}

/**
 * Failures that would repeat on every remaining candidate. A bulk fetch stops
 * on one of these rather than walking the rest of the list to collect the same
 * message a dozen times — and, on a metered source, a rate limit that is
 * already spent means the flights behind it would fail having cost nothing but
 * time anyway.
 */
const HALTING_ERRORS: readonly (SourceError['kind'] | undefined)[] = [
  'auth',
  'no-key',
  'rate-limit',
  'network',
];

type RowState =
  | { kind: 'idle' }
  | { kind: 'fetching' }
  | { kind: 'imported'; trackId: string }
  | { kind: 'duplicate'; message: string; trackId?: string }
  | { kind: 'failed'; message: string };

type BulkState =
  | { kind: 'idle' }
  | { kind: 'running'; done: number; total: number }
  /** `incomplete` covers both endings that leave flights unfetched: stopped, and halted. */
  | { kind: 'done'; imported: number; failed: number; incomplete: boolean };

function bulkSummary(bulk: Extract<BulkState, { kind: 'done' }>): string {
  const parts = [`${String(bulk.imported)} imported.`];
  if (bulk.failed > 0) {
    parts.push(`${String(bulk.failed)} failed.`);
  }
  if (bulk.incomplete) {
    parts.push('Remaining flights not fetched.');
  }
  return parts.join(' ');
}

function candidateDetail(candidate: TrackCandidate): string {
  return [
    candidate.registration ?? candidate.callsign ?? candidate.icaoHex,
    candidate.aircraftType,
    candidate.departure === undefined && candidate.arrival === undefined
      ? undefined
      : `${candidate.departure ?? '?'} → ${candidate.arrival ?? '?'}`,
    candidate.inProgress === true ? 'Still airborne, partial track' : undefined,
  ]
    .filter((part) => part !== undefined && part !== '')
    .join(' · ');
}

export interface CandidateListProps {
  candidates: readonly TrackCandidate[];
  /** The adapter the refs belong to. A ref means nothing to any other source. */
  source: TrackSource | undefined;
  /** What one fetch costs, shown above the list rather than after the click. */
  estimate?: CostEstimate | null;
  /** Replaces the default "N flights found" line, where the caller has better words. */
  heading?: string;
}

export function CandidateList({ candidates, source, estimate, heading }: CandidateListProps) {
  const storedTracks = useLiveQuery(() => trackRepo.listRecent(500), [], null);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});
  const [bulk, setBulk] = useState<BulkState>({ kind: 'idle' });
  /*
   * The running bulk fetch's stop switch, in a ref rather than state: the loop
   * below reads it between fetches, and a state value captured when the run
   * started would never carry the click that happened during it. An
   * `AbortController` only because it is the standard shape for "the thing in
   * flight should stop" — nothing here is an abortable request; the loop
   * simply declines to start the next fetch.
   */
  const stopper = useRef<AbortController | null>(null);

  const runFetch = async (candidate: TrackCandidate): Promise<FetchImportResult | undefined> => {
    if (source === undefined) {
      return undefined;
    }
    setRowState((state) => ({ ...state, [candidate.ref]: { kind: 'fetching' } }));
    // The whole candidate, not just its ref: FR24's registration and hex only
    // exist in the search response this candidate came from.
    const result = await fetchAndImport(source, candidate);
    setRowState((state) => ({
      ...state,
      [candidate.ref]:
        result.outcome === 'imported' && result.track !== undefined
          ? { kind: 'imported', trackId: result.track.id }
          : result.outcome === 'skipped-duplicate'
            ? {
                kind: 'duplicate',
                message: result.message ?? 'Already imported.',
                ...(result.track === undefined ? {} : { trackId: result.track.id }),
              }
            : { kind: 'failed', message: result.message ?? 'This flight could not be fetched.' },
    }));
    return result;
  };

  /**
   * The flights "Fetch all" would actually go and get: everything untouched
   * this session that no stored track already covers. It shrinks as the run
   * proceeds, which is what makes the button disappear once there is nothing
   * left rather than offer to spend credits on flights already on disk.
   */
  const pending = candidates.filter(
    (candidate) =>
      (rowState[candidate.ref] ?? { kind: 'idle' }).kind === 'idle' &&
      alreadyHeld(candidate, storedTracks ?? []) === undefined,
  );

  const runAll = async () => {
    if (source === undefined || pending.length === 0) {
      return;
    }
    const queue = [...pending];
    const stop = new AbortController();
    stopper.current = stop;
    setBulk({ kind: 'running', done: 0, total: queue.length });

    let imported = 0;
    let failed = 0;
    let halted = false;
    for (const candidate of queue) {
      if (stop.signal.aborted) {
        break;
      }
      const result = await runFetch(candidate);
      if (result === undefined) {
        break;
      }
      if (result.outcome === 'imported') {
        imported += 1;
      } else if (result.outcome === 'failed') {
        failed += 1;
        if (HALTING_ERRORS.includes(result.errorKind)) {
          halted = true;
          break;
        }
      }
      setBulk((state) => (state.kind === 'running' ? { ...state, done: state.done + 1 } : state));
    }

    stopper.current = null;
    setBulk({ kind: 'done', imported, failed, incomplete: halted || stop.signal.aborted });
  };

  /*
   * The cost of the button that is on screen. With a bulk fetch offered that
   * is the whole run, not one flight of it — the figure has to match the
   * click, or it is worse than no figure at all.
   */
  const bulkEstimate =
    pending.length > 1 ? (source?.estimateCost?.('fetch', pending.length) ?? null) : null;

  /* A row the run is about to reach is a second charge for the same flight. */
  const running = bulk.kind === 'running';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">
          {heading ??
            `${String(candidates.length)} ${candidates.length === 1 ? 'flight' : 'flights'} found. Nothing has been downloaded yet.`}
        </p>
        {bulk.kind === 'running' ? (
          <span className="flex items-center gap-2">
            <span className="text-muted-foreground tabular text-xs">
              Fetching {Math.min(bulk.done + 1, bulk.total)} of {bulk.total}…
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                stopper.current?.abort();
              }}
            >
              Stop
            </Button>
          </span>
        ) : pending.length > 1 ? (
          <Button
            size="sm"
            onClick={() => {
              void runAll();
            }}
          >
            <CloudArrowDownIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            Fetch all ({pending.length})
          </Button>
        ) : null}
      </div>
      <CostLine estimate={bulkEstimate ?? estimate} />
      {bulk.kind === 'done' ? (
        <p className="text-muted-foreground text-sm" role="status">
          {bulkSummary(bulk)}
        </p>
      ) : null}
      <ul className="border-border rounded-container divide-border divide-y border">
        {candidates.map((candidate) => {
          const state = rowState[candidate.ref] ?? { kind: 'idle' };
          const held = alreadyHeld(candidate, storedTracks ?? []);
          return (
            <li key={candidate.ref} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  {candidate.startTime === undefined
                    ? 'Time unknown'
                    : `${formatLocalDate(candidate.startTime)} · `}
                  {candidate.startTime === undefined ? null : (
                    <span className="tabular">{formatLocalTime(candidate.startTime)}</span>
                  )}
                  {candidate.endTime === undefined ? null : (
                    <>
                      {'–'}
                      <span className="tabular">{formatLocalTime(candidate.endTime)}</span>
                    </>
                  )}
                </p>
                <p className="text-muted-foreground text-xs">{candidateDetail(candidate)}</p>
              </div>

              {state.kind === 'imported' ? (
                <Link
                  to="/flights/$trackId"
                  params={{ trackId: state.trackId }}
                  className="text-sm underline underline-offset-2"
                >
                  Open the imported flight
                </Link>
              ) : state.kind === 'duplicate' ? (
                <span className="text-muted-foreground max-w-60 text-xs">{state.message}</span>
              ) : state.kind === 'failed' ? (
                <span className="text-danger max-w-60 text-xs">{state.message}</span>
              ) : held !== undefined ? (
                <span className="flex items-center gap-2">
                  <Link
                    to="/flights/$trackId"
                    params={{ trackId: held.id }}
                    className="text-muted-foreground text-xs underline underline-offset-2"
                    title="An existing track covers this aircraft and time"
                  >
                    Already have it
                  </Link>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={running || state.kind === 'fetching'}
                    onClick={() => {
                      void runFetch(candidate);
                    }}
                  >
                    Fetch anyway
                  </Button>
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={running || state.kind === 'fetching'}
                  onClick={() => {
                    void runFetch(candidate);
                  }}
                >
                  <CloudArrowDownIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                  {state.kind === 'fetching' ? 'Fetching…' : 'Fetch track'}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
